import type {
  ProposalChangeLine,
  ProposalCollaborationEnvelope,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { isProjectionMetadataPath } from "work/proposals/negotiation/proposal-change-descriptors";
import { proposalChangeValueText } from "work/proposals/negotiation/proposal-change-description";
import { proposalConflictFieldLabel } from "work/proposals/negotiation/proposal-conflict-summary";
import { proposalChangeLineSendsNothing } from "work/proposals/negotiation/proposal-net-zero-change";
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";

/**
 * One pending change a team leader may include in — or hold back from — the next submission.
 *
 * Identity is the conflict key plus, for a collection entry, its stable item ID. That is the same
 * address the server keys resolution targets by, so a selection survives reordering, a refresh, and
 * the server recomputing its own candidate list from the checkpoint diff.
 */
export type PublishableProposalChange = {
  conflictKey: string;
  path: string;
  itemId?: string | null;
  /**
   * The envelope line this was derived from, kept rather than reconstructed. The submit dialog
   * describes it through `describeProposalChangeLine`, and the line is the only thing carrying the
   * value the counterparty can still see — synthesising a stand-in line, as that dialog used to, threw
   * it away and left the dialog showing a new value where every other surface draws a transition.
   */
  line: ProposalChangeLine;
  /** A field name a human recognises, e.g. `/discount/percent` reads as "Discount Percent". */
  label: string;
  /** The value this change would publish, already formatted for display. */
  proposedValue: string;
  /**
   * The value the counterparty last saw at this path, already formatted for display, so the change can
   * be drawn as a redline rather than as a bare new value. Undefined when there is no prior value to
   * strike through — a first submission, or a path the submitted checkpoint never carried.
   */
  previousValue?: string;
};

/**
 * The changes a submission would make counterparty-visible: this party's own work that has not
 * been published yet.
 *
 * `isPublished` is optional on the wire during a rolling deployment. A missing flag is read as
 * "already published" so an older server can only ever produce an empty candidate list — the page
 * then submits with no selection and publishes everything, exactly as it did before this feature.
 * The alternative silence-means-unpublished would offer the leader a tranche of changes the
 * counterparty is already looking at.
 */
export function proposalPublishableChanges(
  envelope: ProposalCollaborationEnvelope,
): PublishableProposalChange[] {
  return envelope.changeLines
    .filter((line) => line.lastChangedByParty === envelope.viewingParty && line.isPublished === false)
    // Projection metadata is not a term. `ProposalResolutionTargetPolicy` says so in as many words —
    // template IDs and lifecycle switches stay in the change-line stream for audit "without creating
    // invisible review work" — and the backend never makes one a resolution target, so publishing is
    // not a decision the leader gets to make about them.
    //
    // Left in, they reached every consumer of this list: the submit confirmation read them back as
    // terms (a bare "Client Team Template Ids" row, and a "Team → No" row that was really the
    // per-viewer masking flag, stacked around the one roster change that meant anything), and the
    // active page's `length > 0` check counted them as something to send.
    .filter((line) => !isProjectionMetadataPath(line.path))
    // A line whose unsent work ends where the counterparty already is sends nothing (#5550): a member
    // a reviewer removed and the leader added back. Offered, it read as "updated" from the member to
    // the member, carried a Revert toggle that restored nothing, and armed Submit on its own.
    .filter((line) => !proposalChangeLineSendsNothing(envelope, line))
    .map((line) => ({
      conflictKey: line.conflictKey,
      path: line.path,
      itemId: line.itemId,
      line,
      label: proposalConflictFieldLabel(line),
      proposedValue: describeProposedValue(envelope, line.path, line.itemId),
      previousValue: describePreviousValue(envelope, line),
    }));
}

/**
 * True when this negotiation opened to revise an already-hired work agreement, so the terms it starts
 * from are a live contract's rather than one party's opening offer.
 *
 * The submit dialog needs this to name what it is showing: a diff against an agreement both sides are
 * already working under, not a summary of a proposal being made for the first time.
 */
export function proposalNegotiationRevisesWorkAgreement(
  envelope: ProposalCollaborationEnvelope | undefined,
): boolean {
  return typeof envelope?.agreedBaselineSequence === "number";
}

/**
 * True when a change line records a term that was already agreed when the negotiation opened, rather
 * than a change either party published during it.
 *
 * A revision of a hired agreement starts with those terms published — they are what the counterparty
 * is working under — which is exactly what keeps them out of `proposalPublishableChanges`. But
 * "something is published" is also how this module detects that a submission has already happened, and
 * an agreed term is not evidence of one. Without the distinction a revision's very first submission
 * would offer per-key tranche checkboxes that the server's initial-submission publication ignores.
 */
export function proposalChangeIsAgreedBaseline(
  envelope: ProposalCollaborationEnvelope,
  line: ProposalChangeLine,
): boolean {
  const agreedThrough = envelope.agreedBaselineSequence;
  return typeof agreedThrough === "number" && line.lastChangedSequence <= agreedThrough;
}

/**
 * True when choosing a tranche is a real choice.
 *
 * On the *initial* submission the recipient deliberately sees no redlining: the server narrows
 * publication to `ProposalResolutionTargetPolicy.InitialSubmissionPaths`, so unchecking the offer's
 * own terms would change nothing a user can observe. Offering checkboxes there would be a lie.
 *
 * A published change line is the evidence that a submission has already happened, which is exactly
 * when every negotiable path starts publishing targets — with one exception: a revision's agreed
 * baseline is published without anyone having submitted anything, so it is excluded here. If a first
 * submission carried no content changes at all there is nothing published to detect, and the
 * counterparty's first counter simply publishes everything — the behaviour that predates this feature.
 */
export function proposalSelectiveTranchePublicationApplies(
  envelope: ProposalCollaborationEnvelope,
): boolean {
  return envelope.changeLines.some(
    (line) => line.isPublished === true && !proposalChangeIsAgreedBaseline(envelope, line),
  );
}

function describeProposedValue(
  envelope: ProposalCollaborationEnvelope,
  path: string,
  itemId?: string | null,
): string {
  return describeValueIn(envelope.projection, path, itemId);
}

/**
 * The value the counterparty can still see at this path — what the change would replace — so the
 * change can be drawn as a redline rather than as a bare new value.
 *
 * Read from the change line itself, which is where the server puts it (`LastSharedValueJson`): the
 * last value that actually went out, whether that was a previous submission's or, for a revision of a
 * hired agreement, the agreed term the negotiation opened from. It used to be read out of
 * `projection.supersedes`, the retired per-submission revision snapshot, which canonical negotiation
 * never populates — so every revision showed a bare new value where the field itself drew a
 * transition. Undefined when the line carries no prior value (a first-time offer's own terms) or when
 * it is identical to what is proposed, since striking through an unchanged value is noise.
 */
function describePreviousValue(
  envelope: ProposalCollaborationEnvelope,
  line: ProposalChangeLine,
): string | undefined {
  if (!line.previousValueExists) return undefined;

  const previous = formatValue(line.path, line.previousValue);
  return previous === describeProposedValue(envelope, line.path, line.itemId) ? undefined : previous;
}

function describeValueIn(
  source: ProposalCollaborationEnvelope["projection"],
  path: string,
  itemId?: string | null,
): string {
  const atPath = readProposalOperationValue(source, path);
  if (!itemId) return formatValue(path, atPath);

  if (!Array.isArray(atPath)) return "Removed";
  const item = atPath.find((entry) => getProposalCollectionItemIdentity(entry) === itemId.toLowerCase());
  return item === undefined ? "Removed" : formatValue(path, item);
}

function formatValue(path: string, value: unknown): string {
  if (value === undefined || value === null || value === "") return "Cleared";
  return proposalChangeValueText(path, value) ?? "Updated";
}
